import type { StaffTeam, StaffTeamRole } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { type ReactNode, useState } from "react";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Avatar } from "../../ui/avatar";
import { Badge, pillClass } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { SelectField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { RadioInput, RadioRow } from "../../ui/radio";
import { StatusBadge } from "../../ui/status-badge";
import { StatusMessage } from "../../ui/status-message";
import { useTeamCapabilities } from "../access";
import { Muted } from "../components/parts";
import { TeamCommandDialog } from "../components/TeamCommandDialog";
import { describeTeamError } from "../errors";
import {
  assignmentTypeLabels,
  roleDescriptions,
  roleLabels,
  roleOrder,
  statusLabels,
} from "../labels";
import { teamKeys, useStaffTeam } from "../queries";
import { useTeamRuntime } from "../runtime";

// The Staff area (CHT-768; administrators). The team directory with the access review, role changes and offboarding. Every change is the
// supported command of the API (`/staff/team/...`): it needs a reason, it sends the version token of the person this screen showed (a stale
// screen is refused and nothing is written, even when the roles look the same again) and it is audited. Hiding a button is not the control:
// the API refuses a role that does not hold the capability. The directory is paged by the server: "Load more" reads the next page, and the
// access review counts come from the server over the whole team, so a page never changes them.
//
// The directory is the kit `DataTable`. Its column list is a module constant and the rows carry what a cell needs: `DataTable` draws a cell
// with `flexRender`, which treats a cell function as a component, so a list made again on every render would throw away every button of the
// table on every render. Every button names its person (hidden words after the visible ones), so a list of the controls has no repeated name.

type Member = StaffTeam["members"][number];
type DialogState =
  | Readonly<{ kind: "roles"; member: Member }>
  | Readonly<{ kind: "offboard"; member: Member }>
  | Readonly<{ kind: "reactivate"; member: Member }>
  | null;

type MemberView = Readonly<{
  member: Member;
  self: boolean;
  canManageRoles: boolean;
  canManageStatus: boolean;
  onRoles: () => void;
  onOffboard: () => void;
  onReactivate: () => void;
}>;

const memberColumn = createColumnHelper<MemberView>();
const memberColumns = [
  memberColumn.display({
    id: "person",
    header: "Person",
    meta: { rowHeader: true },
    cell: ({ row }) => {
      const { member, self } = row.original;
      // A person who is off or has no role is a quiet grey avatar; the others have their own colour.
      const quiet = member.status !== "ACTIVE" || member.roles.length === 0;
      return (
        <span className="flex items-center gap-3">
          <Avatar
            id={member.staffUserId}
            name={member.displayName}
            className={cn("size-9 text-[13px]", quiet && "bg-pill text-pill-foreground")}
          />
          <span
            className={cn(
              "font-serif text-lg font-medium compact:text-base",
              member.status !== "ACTIVE" && "text-muted-foreground",
            )}
          >
            {member.displayName}
            {self ? (
              <span className="font-sans text-sm font-normal text-muted-foreground"> (you)</span>
            ) : null}
          </span>
        </span>
      );
    },
  }),
  memberColumn.display({
    id: "status",
    header: "Status",
    cell: ({ row }) =>
      row.original.member.status === "ACTIVE" ? (
        <StatusBadge tone="success" className={pillClass}>
          {statusLabels.ACTIVE}
        </StatusBadge>
      ) : (
        <StatusBadge tone="neutral" className={pillClass}>
          {statusLabels[row.original.member.status]}
        </StatusBadge>
      ),
  }),
  memberColumn.display({
    id: "roles",
    header: "Roles",
    cell: ({ row }) =>
      row.original.member.roles.length === 0 ? (
        <Muted>No role</Muted>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {row.original.member.roles.map((role) => (
            <li key={role}>
              <Badge tone="outline" className={cn(pillClass, "border-input")}>
                {roleLabels[role]}
              </Badge>
            </li>
          ))}
        </ul>
      ),
  }),
  memberColumn.display({
    id: "covers",
    header: "Covers",
    cell: ({ row }) =>
      row.original.member.status === "ACTIVE" && row.original.member.roles.includes("concierge")
        ? `${row.original.member.activeAssignmentCount} customers`
        : "–",
  }),
  memberColumn.display({
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const { member, self, canManageRoles, canManageStatus, onRoles, onOffboard, onReactivate } =
        row.original;
      const active = member.status === "ACTIVE";
      // The person, in hidden words after the visible ones: "Change roles of Priya Nair". Staff names are shown on this page already.
      const person = <span className="sr-only">of {member.displayName}</span>;
      return self ? (
        <Muted>You cannot change your own access.</Muted>
      ) : (
        <span className="flex flex-wrap gap-2">
          {active && canManageRoles ? (
            <Button type="button" variant="outline" size="sm" onClick={onRoles}>
              <Icon name="user-settings-01" />
              Change roles {person}
            </Button>
          ) : null}
          {active && canManageStatus ? (
            <Button type="button" variant="danger" size="sm" onClick={onOffboard}>
              <Icon name="user-remove-01" />
              Offboard {person}
            </Button>
          ) : null}
          {!active && canManageStatus ? (
            <Button type="button" variant="outline" size="sm" onClick={onReactivate}>
              <Icon name="refresh" />
              Reactivate {person}
            </Button>
          ) : null}
        </span>
      );
    },
  }),
];

export function StaffTeamPage() {
  useWideLayout();
  const { capabilities, isLoading: rolesLoading } = useTeamCapabilities();
  const team = useStaffTeam(capabilities.manageRoles);
  const client = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.manageRoles) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Only an administrator manages staff accounts and roles."
      />
    );
  }

  const reload = () => void client.invalidateQueries({ queryKey: teamKeys.team });
  const finish = (message: string) => {
    setDialog(null);
    setNotice(message);
    reload();
  };

  const views: MemberView[] =
    team.data === undefined
      ? []
      : team.data.members.map((member) => ({
          member,
          self: member.staffUserId === team.data.viewer.staffUserId,
          canManageStatus: team.data.viewer.canManageStatus,
          canManageRoles: team.data.viewer.canManageRoles,
          onRoles: () => setDialog({ kind: "roles", member }),
          onOffboard: () => setDialog({ kind: "offboard", member }),
          onReactivate: () => setDialog({ kind: "reactivate", member }),
        }));

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Staff"
        titleId="page-title"
        description="Who works here, what each person may do, and who still needs the access. Every change needs a reason and is recorded."
      />

      {notice === null ? null : <StatusMessage message={{ tone: "success", text: notice }} />}

      {team.isPending ? (
        <AsyncState kind="loading" title="Loading the team" message="Waiting for the answer." />
      ) : team.isError ? (
        <AsyncState
          kind="error"
          title="The team could not be loaded"
          message={describeTeamError(team.error).message}
          onRetry={() => void team.refetch()}
        />
      ) : (
        <>
          <AccessReview team={team.data} />
          <div className="grid gap-3" aria-busy={team.isFetching}>
            <DataTable<MemberView>
              caption="Staff and their access"
              data={views}
              getRowId={(view) => view.member.staffUserId}
              columns={memberColumns}
              banded="strong"
            />
            <div className="flex flex-wrap items-center gap-3">
              <Muted>
                Showing {team.data.members.length} of {team.data.summary.total} people.
              </Muted>
              {team.hasNextPage ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void team.fetchNextPage()}
                  disabled={team.isFetchingNextPage}
                >
                  {team.isFetchingNextPage ? "Loading…" : "Load more people"}
                </Button>
              ) : null}
            </div>
          </div>
        </>
      )}

      {dialog?.kind === "roles" ? (
        <RolesDialog
          member={dialog.member}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={(message) => finish(message)}
        />
      ) : null}
      {dialog?.kind === "offboard" ? (
        <OffboardDialog
          member={dialog.member}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={(message) => finish(message)}
        />
      ) : null}
      {dialog?.kind === "reactivate" ? (
        <ReactivateDialog
          member={dialog.member}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={(message) => finish(message)}
        />
      ) : null}
    </section>
  );
}

function AccessReview({ team }: Readonly<{ team: StaffTeam }>) {
  // The counts are the server's, over the whole team (not over the pages loaded so far).
  const { activeByRole, activeWithoutRole } = team.summary;
  const count = (role: StaffTeamRole) => activeByRole[role];
  return (
    <section aria-labelledby="team-review-title">
      <Card>
        <CardHeader>
          <CardTitle id="team-review-title" className="text-2xl leading-[30px]">
            Access review
          </CardTitle>
          <CardDescription>
            Each role is the least access for its job. No role includes another. Check that each
            person has only what they need.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-4">
            {roleOrder.map((role) => (
              <div key={role} className="grid content-start gap-1.5">
                <dt className="flex items-center gap-2 text-[15px] font-semibold">
                  {roleLabels[role]}
                  <Badge tone="brass" className="px-[9px] py-px font-semibold tabular-nums">
                    {count(role)}
                  </Badge>
                </dt>
                <dd className="m-0 text-sm leading-5 text-muted-foreground">
                  {roleDescriptions[role]}
                </dd>
              </div>
            ))}
          </dl>
          <ReviewLines
            administrators={count("administrator")}
            activeWithoutRole={activeWithoutRole}
          />
        </CardContent>
      </Card>
    </section>
  );
}

/** What the review found: an amber line for each thing to look at, or one quiet line when there is nothing. */
function ReviewLines({
  administrators,
  activeWithoutRole,
}: Readonly<{
  administrators: number;
  activeWithoutRole: StaffTeam["summary"]["activeWithoutRole"];
}>) {
  const hidden = Math.max(activeWithoutRole.count - activeWithoutRole.names.length, 0);
  return (
    <ul className="m-0 grid list-none gap-2 p-0 text-sm leading-5">
      {administrators === 1 ? (
        <ReviewLine>
          Only one administrator holds the role. They cannot be offboarded or lose it until another
          person has it.
        </ReviewLine>
      ) : null}
      {administrators === 0 ? <ReviewLine>Nobody holds the administrator role.</ReviewLine> : null}
      {activeWithoutRole.count > 0 ? (
        <ReviewLine>
          {activeWithoutRole.count} active{" "}
          {activeWithoutRole.count === 1 ? "person has" : "people have"} no role and no access:{" "}
          {activeWithoutRole.names.join(", ")}
          {hidden > 0 ? ` and ${hidden} more` : ""}.
        </ReviewLine>
      ) : null}
      {administrators > 1 && activeWithoutRole.count === 0 ? (
        <li className="text-muted-foreground">No access problem to review.</li>
      ) : null}
    </ul>
  );
}

function ReviewLine({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <li className="flex items-center gap-2.5 rounded-md border border-warning-border bg-warning-soft px-3.5 py-3 text-warning">
      <Icon name="alert-02" className="size-[18px] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

type DialogProps = Readonly<{
  member: Member;
  onClose: () => void;
  onReload: () => void;
  onDone: (message: string) => void;
}>;

function RolesDialog({ member, onClose, onReload, onDone }: DialogProps) {
  const { api } = useTeamRuntime();
  const held = member.roles;
  const [mode, setMode] = useState<"grant" | "revoke">(
    held.length === roleOrder.length ? "revoke" : "grant",
  );
  const choices = roleOrder.filter((role) =>
    mode === "grant" ? !held.includes(role) : held.includes(role),
  );
  const [role, setRole] = useState<string>("");
  const [roleError, setRoleError] = useState<string | undefined>();
  const chosen = (choices.includes(role as StaffTeamRole) ? role : (choices[0] ?? "")) as
    | StaffTeamRole
    | "";

  return (
    <TeamCommandDialog
      title={`Change roles of ${member.displayName}`}
      description="Roles decide what this person can see and do. The change starts at once and is recorded with your reason."
      confirmLabel={mode === "grant" ? "Give role" : "Take role away"}
      tone={mode === "revoke" ? "danger" : "primary"}
      onClose={onClose}
      onReload={onReload}
      onDone={() =>
        onDone(`${member.displayName}: the role was ${mode === "grant" ? "given" : "taken away"}.`)
      }
      build={() => {
        if (chosen === "") {
          setRoleError("Choose a role.");
          return { ok: false };
        }
        setRoleError(undefined);
        return {
          ok: true,
          send: (reason, key) =>
            (mode === "grant" ? api.grantRole : api.revokeRole)(
              member.staffUserId,
              { role: chosen, reason, expectedVersion: member.version },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <Muted>
        Now: {held.length === 0 ? "no role" : held.map((entry) => roleLabels[entry]).join(", ")}.
      </Muted>
      <fieldset className="m-0 grid gap-1 border-0 p-0">
        <legend className="p-0 font-semibold">What do you want to do?</legend>
        <RadioRow>
          <RadioInput
            name="team-mode"
            checked={mode === "grant"}
            disabled={held.length === roleOrder.length}
            onChange={() => setMode("grant")}
          />
          Give a role
        </RadioRow>
        <RadioRow>
          <RadioInput
            name="team-mode"
            checked={mode === "revoke"}
            disabled={held.length === 0}
            onChange={() => setMode("revoke")}
          />
          Take a role away
        </RadioRow>
      </fieldset>
      <SelectField
        id="team-role"
        label="Role"
        value={chosen}
        error={roleError}
        required
        onChange={setRole}
        options={choices.map((entry) => ({ value: entry, label: roleLabels[entry] }))}
        hint={chosen === "" ? undefined : roleDescriptions[chosen]}
      />
      {mode === "revoke" && chosen === "concierge" ? (
        <Notice tone="warning" className="max-w-3xl">
          Taking the concierge role away ends this person's customer assignments (
          {member.activeAssignmentCount}). Those customers may be left without a primary concierge.
        </Notice>
      ) : null}
    </TeamCommandDialog>
  );
}

function OffboardDialog({ member, onClose, onReload, onDone }: DialogProps) {
  const { api } = useTeamRuntime();
  return (
    <TeamCommandDialog
      title={`Offboard ${member.displayName}`}
      description="This person loses all access now. Use it when they leave or must not work here any more."
      confirmLabel="Offboard"
      tone="danger"
      onClose={onClose}
      onReload={onReload}
      onDone={() =>
        onDone(
          `${member.displayName} was offboarded. Roles, customer assignments, chat access and push notifications were ended.`,
        )
      }
      build={() => ({
        ok: true,
        send: (reason, key) =>
          api.offboard(
            member.staffUserId,
            { reason, expectedVersion: member.version },
            { idempotencyKey: key },
          ),
      })}
    >
      <ul className="m-0 grid list-disc gap-1 pl-5 text-sm leading-6" aria-label="What happens">
        <li>
          {member.roles.length === 0
            ? "No role is left to end."
            : `${member.roles.length} ${member.roles.length === 1 ? "role ends" : "roles end"}: ${member.roles
                .map((role) => roleLabels[role])
                .join(", ")}.`}
        </li>
        <li>
          {member.activeAssignmentCount === 0
            ? "No customer assignment ends."
            : `${member.activeAssignmentCount} customer ${member.activeAssignmentCount === 1 ? "assignment ends" : "assignments end"}. Those customers may be left without a ${assignmentTypeLabels.PRIMARY.toLowerCase()} concierge.`}
        </li>
        <li>
          Chat (Stream) tokens and channel membership are revoked, with retries until it is done.
        </li>
        <li>The push notification identity and every device are revoked.</li>
        <li>
          Their access to this console and the API ends at once. Roles do not come back if they are
          reactivated.
        </li>
      </ul>
    </TeamCommandDialog>
  );
}

function ReactivateDialog({ member, onClose, onReload, onDone }: DialogProps) {
  const { api } = useTeamRuntime();
  return (
    <TeamCommandDialog
      title={`Reactivate ${member.displayName}`}
      description="The account works again, but with no role. Give the roles the person needs afterwards."
      confirmLabel="Reactivate"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`${member.displayName} was reactivated with no role.`)}
      build={() => ({
        ok: true,
        send: (reason, key) =>
          api.reactivate(
            member.staffUserId,
            { reason, expectedVersion: member.version },
            { idempotencyKey: key },
          ),
      })}
    />
  );
}
